import React, { useState } from 'react';
import { Mail, Clock, Send, CheckCircle, Facebook, Instagram } from 'lucide-react';
import { companyInfo } from '../mockData';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: "Meddelande skickat!",
          description: response.data.message,
        });

        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: ''
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte skicka meddelandet. Försök igen senare.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">Kontakta</span> oss
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Vi finns här för att svara på dina frågor och hjälpa dig med dina säkerhetsbehov
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <Mail className="w-10 h-10 text-teal-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">E-post</h3>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-gray-400 hover:text-teal-400 transition-colors"
                >
                  {companyInfo.email}
                </a>
                <p className="text-gray-500 text-sm mt-2">
                  {companyInfo.emergencyNote}
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <Clock className="w-10 h-10 text-teal-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Öppettider</h3>
                <p className="text-gray-400">{companyInfo.openingHours}</p>
                <p className="text-teal-400 text-sm mt-2 font-medium">
                  Akut support: 24/7 via e-post
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Följ Oss</h3>
                <div className="flex space-x-4">
                  <a
                    href={companyInfo.socialMedia.facebook}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-teal-500 transition-all duration-300"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-6 h-6 text-teal-400 hover:text-white" />
                  </a>
                  <a
                    href={companyInfo.socialMedia.instagram}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-teal-500 transition-all duration-300"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-6 h-6 text-teal-400 hover:text-white" />
                  </a>
                </div>
                <p className="text-gray-400 text-sm mt-4">
                  Följ oss för uppdateringar och tips om säkerhet
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                {!isSubmitted ? (
                  <>
                    <h2 className="text-3xl font-bold text-white mb-6">Skicka ett meddelande</h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Namn *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors"
                            placeholder="Ditt namn"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            E-post *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors"
                            placeholder="din@email.se"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Telefon (valfritt)
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors"
                            placeholder="070-123 45 67"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Ämne *
                          </label>
                          <input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors"
                            placeholder="Vad gäller det?"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Meddelande *
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={6}
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors resize-none"
                          placeholder="Beskriv ditt ärende..."
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:shadow-lg hover:shadow-teal-500/50 transition-all duration-300 py-6 text-lg"
                      >
                        {isSubmitting ? 'Skickar...' : 'Skicka meddelande'} <Send className="w-5 h-5 ml-2" />
                      </Button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-20 h-20 text-teal-400 mx-auto mb-6" />
                    <h3 className="text-3xl font-bold text-white mb-4">
                      Tack för ditt meddelande!
                    </h3>
                    <p className="text-gray-400 text-lg">
                      Vi återkommer till dig inom 24 timmar.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">
            Vanliga frågor om privatdetektiv i Sverige
          </h2>
          <div className="space-y-6">
            {[
              {
                q: 'Vad gör en privatdetektiv?',
                a: 'En privatdetektiv hjälper privatpersoner och företag att samla in information, genomföra utredningar och dokumentera bevis. Det kan handla om otrohetsutredningar, bakgrundskontroller, spaning eller andra typer av informationsinsamling. Kontakta oss för att diskutera ditt ärende konfidentiellt.'
              },
              {
                q: 'När bör man anlita en privatdetektiv?',
                a: 'Du bör anlita en privatdetektiv när du behöver klarhet eller bevis i en situation, till exempel vid misstänkt otrohet, trakasserier eller bedrägeri. Kontakta oss så hjälper vi dig avgöra om en utredning är rätt steg.'
              },
              {
                q: 'Hur anlitar man en privatdetektiv?',
                a: 'Du kontaktar oss via formulär eller e-post och beskriver ditt ärende. Därefter gör vi en konfidentiell bedömning och återkommer med ett förslag på upplägg och pris. Kontakta oss idag för en första bedömning.'
              },
              {
                q: 'Vad kostar en privatdetektiv i Sverige?',
                a: 'Priset beror på uppdragets omfattning, tidsåtgång och komplexitet. Enklare ärenden kan gå snabbt medan mer avancerade utredningar kräver mer resurser. Vi erbjuder alltid en kostnadsfri och konfidentiell konsultation där du får en tydlig offert innan vi påbörjar arbetet.'
              },
              {
                q: 'Är det lagligt att anlita en privatdetektiv i Sverige?',
                a: 'Ja, det är lagligt att anlita en privatdetektiv i Sverige. Vi arbetar alltid enligt svensk lag och använder endast metoder som är juridiskt hållbara. Kontakta oss om du vill veta vad som är möjligt i ditt specifika fall.'
              },
              {
                q: 'Hur diskret arbetar ni?',
                a: 'Diskretion är en central del av vårt arbete. Alla uppdrag hanteras strikt konfidentiellt och ingen information delas utan ditt samtycke. Din integritet är alltid vår högsta prioritet.'
              },
              {
                q: 'Kan jag vara anonym när jag kontaktar er?',
                a: 'Ja, du kan vara anonym vid första kontakten. Vi respekterar din integritet och anpassar processen efter din trygghet. Kontakta oss utan förpliktelser.'
              },
              {
                q: 'Hur snabbt kan ni börja?',
                a: 'I många fall kan vi påbörja arbetet snabbt efter att vi gått igenom ditt ärende. Akuta ärenden kan prioriteras. Kontakta oss så återkommer vi så snart som möjligt.'
              },
              {
                q: 'Hur lång tid tar en utredning?',
                a: 'Tiden varierar beroende på uppdragets omfattning. Enklare ärenden kan vara klara inom 24–48 timmar medan mer omfattande utredningar kan ta längre tid. Du får alltid en tidsuppskattning innan vi börjar.'
              },
              {
                q: 'Kan ni hjälpa vid misstänkt otrohet?',
                a: 'Ja, vi hjälper regelbundet klienter som misstänker otrohet. Vi kan samla in bevis och ge dig tydlig information om situationen på ett diskret sätt. Kontakta oss för att diskutera din situation.'
              },
              {
                q: 'Hur går en otrohetsutredning till?',
                a: 'Vi börjar med en konsultation där vi går igenom din situation. Därefter planerar vi utredningen, genomför observation och dokumentation och sammanställer resultatet i en rapport. Vi guidar dig genom hela processen.'
              },
              {
                q: 'Kan era bevis användas i domstol?',
                a: 'Ja, vi dokumenterar våra utredningar på ett sätt som gör att materialet kan användas i juridiska sammanhang vid behov. Vi säkerställer att bevisen håller hög kvalitet.'
              },
              {
                q: 'Får jag en rapport efter utredningen?',
                a: 'Ja, du får alltid en tydlig och detaljerad rapport med all relevant information och dokumentation från uppdraget. Rapporten kan användas som beslutsunderlag eller juridiskt stöd.'
              },
              {
                q: 'Vad händer om ni inte hittar något?',
                a: 'Du får alltid en rapport som visar vad som har undersökts. Vi är transparenta med resultatet, oavsett utfall. Även avsaknad av resultat kan ge viktig klarhet.'
              },
              {
                q: 'Arbetar ni i hela Sverige?',
                a: 'Ja, vi arbetar över hela Sverige och kan snabbt mobilisera resurser oavsett var uppdraget finns. Kontakta oss så planerar vi insatsen.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-3">{faq.q}</h3>
                <p className="text-gray-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
